const HOUR_BASE = 60, DAY_BASE = 60 * 24, WEEK_BASE = 60 * 24 * 7, MONTH_BASE = 60 * 24 * 30, YEAR_BASE = 525600;
const MINUTES_MAX = 5184000;
const RPO_MINUTES_MAX = 1512000;  // 35 months, because RPO must be at least two times smaller then retention

class PoliciesUtilsService {
    constructor() {
        this.validationValues = {
            minutes: MINUTES_MAX,
            hours: MINUTES_MAX / HOUR_BASE,
            days: MINUTES_MAX / DAY_BASE,
            weeks: MINUTES_MAX / WEEK_BASE,
            months: MINUTES_MAX / MONTH_BASE,
            base: 999
        };

        this.validationRPOValues = {
            minutes: RPO_MINUTES_MAX,
            hours: RPO_MINUTES_MAX / HOUR_BASE,
            days: RPO_MINUTES_MAX / DAY_BASE,
            weeks: RPO_MINUTES_MAX / WEEK_BASE,
            months: RPO_MINUTES_MAX / MONTH_BASE,
            base: 999
        };
    }

    calculateRtoRpoTime(time) {
        let calc = 'MINUTES';
        if ((time >= HOUR_BASE) && (time % HOUR_BASE === 0)) calc = 'HOURS';
        if ((time >= DAY_BASE) && (time % DAY_BASE === 0)) calc = 'DAYS';
        if ((time >= WEEK_BASE) && (time % WEEK_BASE === 0)) calc = 'WEEKS';
        if (this.getNumberOfMonths(time).remainder === 0) calc = 'MONTHS';
        return calc;
    }

    calculateRetentionTime(time) {
        let calc = 'DAYS';
        if ((time >= WEEK_BASE) && (time % WEEK_BASE === 0)) calc = 'WEEKS';
        if (this.getNumberOfMonths(time).remainder === 0) calc = 'MONTHS';
        return calc;
    }

    readRtoRpo(value, time) {
        return time === 'HOURS' ? (value / HOUR_BASE) :
            time === 'DAYS' ? (value / DAY_BASE) :
                time === 'WEEKS' ? (value / WEEK_BASE) :
                    time === 'MONTHS' ? this.getNumberOfMonths(value).numOfMonths :
                        time === 'YEARS' ? this.getNumberOfYears(value) :
                            value;
    }

    writeRtoRpo(value, time) {
        return time === 'HOURS' ? (value * HOUR_BASE) :
            time === 'DAYS' ? (value * DAY_BASE) :
                time === 'WEEKS' ? (value * WEEK_BASE) :
                    time === 'MONTHS' ? this.getMonthBase(value) :
                        time === 'YEARS' ? this.getYearBase(value) :
                            value;
    }

    setRpoRtoValidation(timeItem, isRPO) {
        let temp = isRPO ? this.validationRPOValues : this.validationValues;
        return timeItem.value === 'HOURS' ? temp.hours :
            timeItem.value === 'DAYS' ? temp.days :
                timeItem.value === 'WEEKS' ? temp.weeks :
                    timeItem.value === 'MONTHS' ? temp.months :
                        temp.minutes;
    }

    setMinRpoRtoValidation(timeItem, snapOnly) {
        if (snapOnly) {
            return timeItem.value === 'MINUTES' ? 30 : 1;
        } else {
            return timeItem.value === 'MINUTES' ? 60 : 1;

        }
    }

    readRetention(value, time) {
        return time === 'WEEKS' ? value / WEEK_BASE : (time === 'MONTHS' ? this.getNumberOfMonths(value).numOfMonths : value / DAY_BASE);
    }

    writeRetention(value, time) {
        return time === 'WEEKS' ? value * WEEK_BASE : (time === 'MONTHS' ? this.getMonthBase(value) : value * DAY_BASE);
    }

    setRetValidation(retTime) {
        return retTime.value === 'WEEKS' ? Math.floor(this.validationValues.weeks) : (retTime.value === 'MONTHS' ? Math.floor(this.validationValues.months) : Math.floor(this.validationValues.days));
    }

    setRetMinValidation(retTime, rpoItem) {
        let response = this.roundToGreaterValue(this.readRetention(rpoItem * 2, retTime.value));
        return response > 1 ? response : 1;
    }

    setCopyRetMinValidation(copyRetTime, retItem) {
        let response = this.roundToGreaterValue(this.readRetention(retItem, copyRetTime.value));
        return response > 1 ? response : 1;
    }

    setSnapshotRetMinValidation(sRetTime, rpoItem) {
        let response = this.roundToGreaterValue(this.readRtoRpo(rpoItem, sRetTime.value));
        return response > 1 ? response : 1;
    }

    setSnapshotRetMaxValidation(sRetTime, rpoItem) {
        let response = this.roundToSmallerValue(this.readRtoRpo(rpoItem, sRetTime.value));
        return response >= 1 ? response : 0;
    }

    roundValue(num) {
        return Math.round(num * 100) / 100;
    }

    roundToSmallerValue(num) {
        return Math.floor(num);
    }

    roundToGreaterValue(num) {
        return Math.ceil(num);
    }

    getMonthBase(months) {
        let date = new Date(),
            monthBase = 0;
        for (let i = 0; i < months; i++) {
            let daysInMonth = this.getDaysInMonth(date.getMonth() + 1 + i, date.getFullYear());
            monthBase += 60 * 24 * daysInMonth;
        }
        return monthBase;
    }

    getNumberOfMonths(value) {
        let remainder = value,
            numOfMonths = 0,
            date = new Date();
        while (remainder > 0) {
            remainder -= (60 * 24 * this.getDaysInMonth(date.getMonth() + 1 + numOfMonths, date.getFullYear()));
            numOfMonths++;
        }
        return { remainder: remainder, numOfMonths: numOfMonths };
    }

    getDaysInMonth(month, year) {
        return new Date(year, month, 0).getDate();
    }

    getYearBase(years) {
        let date = new Date(),
            yearBase = 0,
            currentYear;
        for (let i = 0; i < years; i++) {
            currentYear = date.getFullYear() + i;
            yearBase += (this.isLeapYear(currentYear) ? 366 : 365) * 24 * 60;
        }
        return yearBase;
    }

    getNumberOfYears(value) {
        let remainder = value,
            numOfYears = 0,
            date = new Date(),
            currentYear;
        while (remainder > 0) {
            currentYear = date.getFullYear() + numOfYears;
            remainder -= ((this.isLeapYear(currentYear) ? 366 : 365) * 24 * 60);
            numOfYears++;
        }
        return numOfYears;
    }

    isLeapYear(year) {
        return (year % 100 === 0) ? (year % 400 === 0) : (year % 4 === 0);
    }
}

module.exports = PoliciesUtilsService;



// WEBPACK FOOTER //
// ./src/azurebackup/views/policies/policies.utils.js